import {
  IExpressionFunction,
  IFieldArray,
  IFieldRuntimeObject,
  ITemplateOptions,
  IValidator,
  IWatcher,
} from "AngularFormly";
import fieldHelpers from "../../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../records/forms/formly/fieldConfigurationHelpers";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { MachineAndConfigurationThreeRivers } from "../../models/machine";
import {
  MachineGameConfigurationThreeRivers,
  MachineProgressiveLevel,
} from "../../models/machineConfiguration";
import { ITemplateScope } from "AngularFormly";
import { get, set } from "lodash";
import { IOnInit, isArray, IScope, ITimeoutService } from "angular";
import { MachineDenomination } from "../../../shared/models/machineConfiguration";
import {
  LookupModalService,
  vsServiceName as LookupModalServiceName,
} from "../../../../records/lookup/lookupModal";
import {
  UserProfile,
  vsName as UserProfileName,
} from "../../../../application/UserProfile";

import * as lookupNames from "../../configs/lookup";
import { StateService } from "@uirouter/angularjs";
import { ensureEnabledQueryBuilder } from "../../../../records/forms/formly/types/recordSelectAsync/utils";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../../../records/views/recordGridView";
import { vsObjectName as epromObjectName } from "../../configs/machineEpromConfigurations";
import { LookupManagementService } from "../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName } from "../../../shared/lookups";

function getQueryUrl(name: string) {
  return `/api/machines/${name}`;
}

export class MachineEditFormComponentController implements IOnInit {
  static $inject = [
    FieldConfigurationHelperServiceName,
    ServerUtilityName,
    LookupModalServiceName,
    UserProfileName,
    "$state",
    RecordGridViewServiceName,
    vsLookupManagementServiceName,
  ];

  /*bindings*/
  model!: MachineAndConfigurationThreeRivers;
  machineFieldsEditable!: boolean;
  /*end bindings */

  readonly formFields: IFieldArray;

  constructor(
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    private readonly serverUtility: ServerUtility,
    private readonly lookupModalService: LookupModalService,
    private readonly userProfile: UserProfile,
    private readonly $state: StateService,
    private readonly recordGridViewService: RecordGridViewService,
    private readonly lookupManagementService: LookupManagementService
  ) {
    this.formFields = this.initializeFormFields();
  }

  $onInit(): void {}

  getDropdownAddAddon(
    lookupName: string,
    valueSetter: (value: any, scope: IScope) => void,
    lookupModelFactory?: (
      options: ITemplateOptions,
      model: any,
      scope: IScope
    ) => Promise<any>
  ) {
    return this.lookupModalService.getDropdownAddAddon(
      this.lookupManagementService,
      lookupName,
      valueSetter,
      "machineLookupModify",
      lookupModelFactory
    );
  }

  private initializeFormFields(): IFieldArray {
    const epromGridConfiguration =
      this.recordGridViewService.getGridConfiguration(epromObjectName)!;
    return [
      fieldHelpers.headerField({
        headerText: "Date/Location",
        key: "DateLocation",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: this.configurationFieldKey("Date"),
            label: "Date",
            required: true,
          }),
          fieldHelpers.simpleDateField({
            key: this.configurationFieldKey("InServiceDate"),
            label: "In Service Date",
          }),
          fieldHelpers.simpleDateField({
            key: this.machineFieldKey("PurchaseDate"),
            label: "Purchase Date",
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.selectAsyncField({
            queryUrl: "/api/properties",
            getDefaultItem: (model: any) =>
              MachineEditFormComponentController.getDefaultItem(
                get(model, this.configurationFieldKey("Property"))
              ),
            label: "Property",
            key: this.configurationFieldKey("PropertyId"),
            required: true,
          }),
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: this.configurationFieldKey("MachineAreaId"),
              objectName: lookupNames.areaName,
              expressionProperties: this.requireIfStatus("Floor"),
              hideExpression: this.hideIfNotStatus("Floor"),
              watcher: this.watchAndSetUndefinedIfNotStatus("Floor", [
                this.configurationFieldKey("MachineAreaId"),
                this.configurationFieldKey("MachineArea"),
              ]),
            }
          ),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("Section"),
            label: "Section",
            expressionProperties: this.requireIfStatus("Floor"),
            hideExpression: this.hideIfNotStatus("Floor"),
            watcher: this.watchAndSetUndefinedIfNotStatus(
              "Floor",
              this.configurationFieldKey("Section")
            ),
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("Bank"),
            label: "Bank",
            expressionProperties: this.requireIfStatus("Floor"),
            hideExpression: this.hideIfNotStatus("Floor"),
            watcher: this.watchAndSetUndefinedIfNotStatus(
              "Floor",
              this.configurationFieldKey("Bank")
            ),
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("Position"),
            label: "Position",
            expressionProperties: this.requireIfStatus("Floor"),
            hideExpression: this.hideIfNotStatus("Floor"),
            watcher: this.watchAndSetUndefinedIfNotStatus(
              "Floor",
              this.configurationFieldKey("Position")
            ),
          }),
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: this.configurationFieldKey("MachineBankConfigId"),
              label: "Bank Config",
              expressionProperties: this.requireIfStatus("Floor"),
              hideExpression: this.hideIfNotStatus("Floor"),
              watcher: this.watchAndSetUndefinedIfNotStatus("Floor", [
                this.configurationFieldKey("MachineBankConfigId"),
                this.configurationFieldKey("MachineBankConfig"),
              ]),
              objectName: lookupNames.machineBankConfigName,
            }
          ),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("WarehouseLocation"),
            label: "Warehouse Location",
            expressionProperties: this.requireIfStatus("Warehoused"),
            hideExpression: this.hideIfNotStatus("Warehoused"),
            watcher: this.watchAndSetUndefinedIfNotStatus(
              "Warehoused",
              this.configurationFieldKey("WarehouseLocation")
            ),
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "EGM Information",
        key: "EGMInformation",
      }),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: this.machineFieldKey("MachineManufacturerId"),
              objectName: lookupNames.manufacturerName,
              expressionProperties: this.getMachineFieldExpressionProperties(),
              required: true,
            }
          ),
          fieldHelpers.simpleStringField({
            key: this.machineFieldKey("SerialNumber"),
            expressionProperties: this.getMachineFieldExpressionProperties(),
            label: "Serial Number",
            required: true,
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("SlotNumber"),
            label: "Asset Number",
            expressionProperties: this.requireIfStatus("Floor"),
          }),
          {
            key: this.configurationFieldKey("Status"),
            name: "Status",
            type: "select",
            templateOptions: {
              label: "Status",
              options: [
                {
                  label: "Floor",
                  value: "Floor",
                },
                {
                  label: "Warehoused",
                  value: "Warehoused",
                },
                {
                  label: "Decommissioned",
                  value: "Decommissioned",
                },
              ],
              required: true,
              labelProp: "label",
              valueProp: "value",
            },
          },
          fieldHelpers.simpleBoolField({
            key: this.configurationFieldKey("CameraCoverage"),
            label: "Camera Coverage",
            defaultValue: false,
          }),
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: this.machineFieldKey("MachineCabinetTypeId"),
              objectName: lookupNames.cabinetTypeName,
              expressionProperties: this.getMachineFieldExpressionProperties(),
              required: true,
            }
          ),
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: this.machineFieldKey("MachineModelId"),
              expressionProperties: this.getMachineFieldExpressionProperties(),
              objectName: lookupNames.modelName,
              required: true,
            }
          ),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("NumberOfReels"),
            label: "Number of Reels",
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("TgraGovStamp"),
            label: "TGRA # - Gov Stamp",
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Game Configurations",
        key: "GameConfigurationsHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          {
            ...this.fieldConfigurationHelperService.selectAsyncField({
              key: this.configurationFieldKey("MachineTypeIdId"),
              label: "Type ID",
              queryUrl: "/api/machines/MachineTypeId",
              getDefaultItem: (model: any) =>
                MachineEditFormComponentController.getDefaultItem(
                  get(model, this.configurationFieldKey("MachineTypeId"))
                ),
            }),
            expressionProperties: this.requireIfStatus("Floor"),
          },
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Progressive Information",
        key: "ProgressiveInformationHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("ProgressiveController"),
            label: "Progressive Controller",
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("ControllerLocation"),
            label: "Controller Location",
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("ProgressiveSerialNumber"),
            label: "Progressive Serial Number",
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("ControllerType"),
            label: "Controller Type",
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Lease Information",
        key: "LeaseInformationHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleBoolField({
            key: this.configurationFieldKey("Leased"),
            label: "Leased",
            defaultValue: false,
          }),
          fieldHelpers.simpleBoolField({
            key: this.configurationFieldKey("LeasedSoftware"),
            label: "Leased Software",
            defaultValue: false,
          }),
          fieldHelpers.simpleStringField({
            key: this.configurationFieldKey("SpecialtyPupd"),
            label: "Specialty PUPD",
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: this.configurationFieldKey("MachineLeaseFeeId"),
              objectName: lookupNames.leaseFeeName,
            }
          ),
          this.fieldConfigurationHelperService.lookupField(
            this.lookupManagementService,
            {
              key: this.configurationFieldKey("MachineLeaseTypeId"),
              objectName: lookupNames.leaseTypeName,
            }
          ),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Eproms",
        key: "EpromHeader",
      }),
      this.fieldConfigurationHelperService.repeaterField({
        key: this.configurationFieldKey("Eproms"),
        allowAdd: true,
        allowAddBottom: true,
        displayNameSingular: epromGridConfiguration.nameSingular ?? "Eprom",
        displayNamePlural: epromGridConfiguration.namePlural ?? "Eproms",
        staticFields: [
          fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleReadonlyField({
                key: this.configurationFieldKey("MachineEpromLabel"),
                label: "Type",
                formatters: [() => "Machine EPROM"],
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("MachineEprom"),
                label: "Eprom",
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("MachineEpromVersion"),
                label: "Version",
              }),
            ],
            4
          ),
          fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleReadonlyField({
                key: this.configurationFieldKey("BootBiosLabel"),
                label: "Type",
                formatters: [() => "BOOT/BIOS"],
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("BootBios"),
                label: "Eprom",
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("BootBiosVersion"),
                label: "Version",
              }),
            ],
            4
          ),
          fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleReadonlyField({
                key: this.configurationFieldKey("PrinterType"),
                label: "Type",
                formatters: [() => "Printer"],
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("Printer"),
                label: "Eprom",
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("PrinterVersion"),
                label: "Version ",
              }),
            ],
            4
          ),
          fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleReadonlyField({
                key: this.configurationFieldKey("BvGlCodeLabel"),
                label: "Type",
                formatters: [() => "BV-G/L Code"],
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("BvGlCode"),
                label: "Eprom",
              }),
              fieldHelpers.simpleStringField({
                key: this.configurationFieldKey("BvGlCodeVersion"),
                label: "Version",
              }),
            ],
            4
          ),
        ],
        repeaterFields: fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleStringField({
              key: "EpromType",
              label: "Type",
              required: true,
            }),
            fieldHelpers.simpleStringField({
              key: "EpromLabel",
              label: "Eprom",
              required: true,
            }),
            fieldHelpers.simpleStringField({
              key: "EpromVersion",
              label: "Version",
            }),
            {
              key: "copyDelete",
              type: "recordRepeaterCopyDelete",
              className: "col-xs-6",
              templateOptions: {
                label: " ",
                allowMove: true,
              },
            },
          ],
          4
        ),
        showHeader: false,
      }),
      fieldHelpers.headerField({
        headerText: "Limit Settings",
        key: "LimitSettingsHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleCurrencyField({
            key: this.configurationFieldKey("CreditLimit"),
            label: "Credit Limit",
          }),
          fieldHelpers.simpleCurrencyField({
            key: this.configurationFieldKey("JackpotLimit"),
            label: "Jackpot Limit",
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Files",
        key: "FileHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleSingleFileUpload({
            key: this.configurationFieldKey("ParSheet"),
            label: "Par Sheet",
          }),
          fieldHelpers.simpleSingleFileUpload({
            key: this.configurationFieldKey("ProgressiveSetupForm"),
            label: "Progressive Setup Form",
          }),
          fieldHelpers.simpleSingleFileUpload({
            key: this.configurationFieldKey("InstallSheet"),
            label: "Install Sheet",
          }),
          fieldHelpers.simpleSingleFileUpload({
            key: this.configurationFieldKey("MultiDenomSheet"),
            label: "Multi Denom Sheet",
          }),
          fieldHelpers.simpleSingleFileUpload({
            key: this.configurationFieldKey("GliBmm"),
            label: "GLI/BMM",
          }),
          fieldHelpers.simpleSingleFileUpload({
            key: this.configurationFieldKey("ProgressiveDisbursements"),
            label: "Progressive Disbursements",
          }),
          fieldHelpers.simpleSingleFileUpload({
            key: this.configurationFieldKey("ConversionMoves"),
            label: "Conversion/Moves?",
          }),
        ],
        4
      ),
      {
        key: this.configurationFieldKey("Files"),
        type: "recordFileUpload",
        templateOptions: {
          label: "Files",
          allowMultiple: true,
          hideLabel: true,
        },
      },
      fieldHelpers.headerField({
        headerText: "Notes",
        key: "NotesHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.textAreaField({
            key: this.configurationFieldKey("Notes"),
            label: " ",
            rows: 5,
          }),
        ],
        1
      ),
    ];
  }

  private requireIfStatus(status: string) {
    return {
      "templateOptions.required": () =>
        this.model.Configuration.Status === status,
    };
  }

  private hideIfNotStatus(status: string) {
    return (_: any, __: any, scope: ITemplateScope) =>
      get(scope.model, this.configurationFieldKey("Status")) !== status;
  }

  private getMachineFieldExpressionProperties(): {
    [key: string]: string | IExpressionFunction | IValidator;
  } {
    return {
      "templateOptions.readonly": () => !this.machineFieldsEditable,
    };
  }

  configurationFieldKey(fieldName: string) {
    return `Configuration.${fieldName}`;
  }

  machineFieldKey(fieldName: string) {
    return `Machine.${fieldName}`;
  }

  static getDefaultItem(model: any) {
    return {
      Id: model?.Id,
      Name: model?.Name,
    };
  }

  private watchAndSetUndefinedIfNotStatus(
    status: string,
    field: string | string[]
  ): IWatcher {
    return {
      expression: () => this.model.Configuration.Status !== status,
      listener: this.blankFieldListener(field),
    };
  }

  private blankFieldListener(
    field: string | string[]
  ): (
    field: IFieldRuntimeObject,
    newValue: any,
    oldValue: any,
    scope: ITemplateScope,
    stopWatching: Function
  ) => void {
    const fields = isArray(field) ? <string[]>field : [<string>field];
    return (_, newValue, ___, scope) => {
      if (newValue) {
        fields.forEach((f) => set(scope.model ?? {}, f, undefined));
      }
    };
  }
}
